import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelloSign from 'hellosign-embedded';
import PropTypes from 'prop-types';
import { AppModal } from '../AppModal';
import {
  previewPlutoConsent,
  fetchPlutoConsentPdfBlob,
} from '../../services/lighthouse.service';

/**
 * Verify → Pluto row action (proto): open the Pluto Dropbox consent document.
 * Patient creation is NOT shown here — it runs automatically after ICF countersign.
 */
export function PlutoConsentModal({ open, onClose, token, userId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [embedReady, setEmbedReady] = useState(false);
  const clientRef = useRef(null);
  const containerRef = useRef(null);

  const closeClient = () => {
    if (!clientRef.current) return;
    try {
      clientRef.current.close();
    } catch {
      // already closed
    }
    clientRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setError(null);
      setEmbedReady(false);
      closeClient();
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      setPdfUrl(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- revoke prior url on close only

  useEffect(() => {
    if (!open || !userId || !token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmbedReady(false);

    (async () => {
      const { data, error: err } = await previewPlutoConsent(token, userId);
      if (cancelled) return;
      if (err) {
        setLoading(false);
        setError(typeof err === 'string' ? err : err?.message || 'Failed to load Pluto consent');
        return;
      }
      setPreview(data);
      if (!data?.eligible) {
        setLoading(false);
        return;
      }

      if (data.mode === 'embed' && data.sign_url) {
        setEmbedReady(true);
        setLoading(false);
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        if (cancelled || !containerRef.current) return;

        closeClient();
        const clientId = data.client_id || process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID;
        const client = new HelloSign({ clientId });
        clientRef.current = client;
        client.on('close', () => {
          clientRef.current = null;
        });
        client.on('error', (hsErr) => {
          console.error('HelloSign error', hsErr);
          if (!cancelled) setError('Signing was interrupted. Close and try again.');
        });
        try {
          client.open(data.sign_url, {
            clientId,
            skipDomainVerification: true,
            container: containerRef.current,
          });
        } catch (e) {
          if (!cancelled) {
            setEmbedReady(false);
            setError(e?.message || 'Failed to open Dropbox Sign');
          }
        }
        return;
      }

      const { blob, error: pdfErr } = await fetchPlutoConsentPdfBlob(token, userId);
      if (cancelled) return;
      if (pdfErr || !blob) {
        setLoading(false);
        setError(pdfErr || 'Failed to load Pluto consent PDF');
        return;
      }
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      closeClient();
    };
  }, [open, userId, token]);

  const officialName = preview?.name || '—';
  const dob = preview?.dob || '—';
  const email = preview?.email || '—';

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={null}
      showCloseButton={false}
      actions={null}
      dividers={false}
      maxWidth="lg"
      className="onyx-document-modal"
      aria-label="Pluto consent"
    >
      <div className="onyx-sign-icf" data-testid="onyx-pluto-consent">
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="onyx-sign-icf__close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <div className="onyx-sign-icf__header">
          <span className="onyx-sign-icf__meta-label">Official name</span>
          <span className="onyx-sign-icf__meta-value">{officialName}</span>
          <span className="onyx-sign-icf__meta-label">DOB</span>
          <span className="onyx-sign-icf__meta-value">{dob}</span>
          <span className="onyx-sign-icf__meta-label">Email</span>
          <span className="onyx-sign-icf__meta-value">{email}</span>
        </div>

        <div className="onyx-sign-icf__document">
          {loading ? (
            <div className="onyx-sign-icf__status">
              <CircularProgress size={28} />
              <Typography variant="uiBody">Loading Pluto consent…</Typography>
            </div>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!loading && preview && !preview.eligible ? (
            <Alert severity="info">
              {preview.error || 'No Pluto consent for this member.'}
            </Alert>
          ) : null}
          {pdfUrl ? (
            <iframe
              title="Pluto consent"
              src={pdfUrl}
              className="onyx-sign-icf__embed onyx-sign-icf__embed--active"
              data-testid="onyx-pluto-consent-pdf"
            />
          ) : (
            <div
              ref={containerRef}
              className={`onyx-sign-icf__embed${embedReady ? ' onyx-sign-icf__embed--active' : ''}`}
              data-testid="onyx-pluto-consent-embed"
              aria-hidden={!embedReady}
            />
          )}
        </div>
      </div>
    </AppModal>
  );
}

PlutoConsentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  token: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PlutoConsentModal;
